import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiCopy } from 'react-icons/fi';
import apiService from '../services/apiService';
import './EmailTemplateDetails.css';

const EmailTemplateDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');

  useEffect(() => {
    if (id) {
      fetchEmailTemplateDetails();
    }
  }, [id]);

  const fetchEmailTemplateDetails = async () => {
    try {
      setLoading(true);
      const data = await apiService.get(`/email-template/${id}`);
      setTemplateData(data);
      setEmailSubject(data.emailSubject || '');
      setEmailBody(data.emailBody || '');
      setError(null);
    } catch (err) {
      console.error('Error fetching email template details:', err);
      setError('Failed to load email template details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(emailBody);
      alert('Email body copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = emailBody;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Email body copied to clipboard!');
      } catch (e) {
        alert('Failed to copy to clipboard. Please select and copy manually.');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const requestBody = {
        emailSubject: emailSubject,
        emailBody: emailBody
      };

      await apiService.put(`/email-template/${id}`, requestBody);
      
      alert('Email template updated successfully!');
      
    } catch (err) {
      console.error('Error updating email template:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update email template. Please try again.';
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleSendToMe = async () => {
    try {
      setSending(true);
      
      await apiService.post(`/email-template/send?id=${id}`, {
        templateId: parseInt(id)
      });
      
      alert('Email sent successfully!');
      
    } catch (err) {
      console.error('Error sending email:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send email. Please try again.';
      alert(errorMessage);
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Email Template Details</h1>
        </div>
        <div className="loading-state">
          <p>Loading email template details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Email Template Details</h1>
        </div>
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => navigate('/email-templates')} className="retry-button">
            Back to Email Templates
          </button>
        </div>
      </div>
    );
  }

  if (!templateData) {
    return (
      <div className="manage-properties">
        <div className="page-header">
          <h1>Email Template Details</h1>
        </div>
        <div className="error-state">
          <p>Email template not found</p>
          <button onClick={() => navigate('/email-templates')} className="retry-button">
            Back to Email Templates
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-properties">
      <div className="page-header">
        <h1>Email Template Details</h1>
      </div>

      <div className="content-container">
        <div className="form-section">
          <h3>Template Information</h3>
          <div className="template-header-row">
            <div className="template-title-section">
              <div className="template-title-label">Title:</div>
              <div className="template-title-value">
                {templateData.title || 'N/A'}
              </div>
            </div>
            {templateData.tokenTags && (
              <div className="template-tokens-section">
                <div className="template-tokens-label">Available Tokens:</div>
                <ul className="token-tags-list">
                  {templateData.tokenTags.split(',').map((tag, index) => (
                    <li key={index}>
                      {tag.trim()}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Email Content</h3>
          <div className="form-row">
            <label htmlFor="emailSubject">Email Subject:</label>
            <input
              type="text"
              id="emailSubject"
              className="form-input"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Enter email subject"
            />
          </div>

          <div className="form-row email-body-row">
            <label htmlFor="emailBody">Email Body:</label>
            <div className="email-body-wrapper">
              <button
                type="button"
                onClick={handleCopyToClipboard}
                className="copy-button"
                title="Copy email body to clipboard"
              >
                <FiCopy size={16} />
                Copy
              </button>
              <textarea
                id="emailBody"
                className="form-textarea email-body-textarea"
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                rows="20"
                placeholder="Enter email body"
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button 
            type="button" 
            className="btn-primary"
            onClick={handleSendToMe}
            disabled={sending}
          >
            {sending ? 'Sending...' : 'Send to me'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateDetails;

